import { ProfileButton } from '@auth/components/profile-button';
import { useLogOut } from '@auth/hooks/use-log-out/index.ts';
import { AnonymousContextConfirmationModal } from '@common/components/anonymous-context-confirmation-modal/anonymous-context-confirmation-modal.tsx';
import { ContextSwitcherButton } from '@common/components/context-switcher-button';
import { DocumentNavigationBar } from '@common/components/document-navigation-bar';
import { useMailNotVerifiedCountContext } from '@common/context/email-not-verified-count-context/index.ts';
import { useLang } from '@common/context/language-provider/index.ts';
import { useLegacyFunction } from '@common/context/legacy-function-provider/index.ts';
import { useSubNavigationContext } from '@common/context/sub-navigation-context-provider';
import { useUserContext } from '@common/context/user/user-context.ts';
import { SubNavigationEntry } from '@common/types/sub-navigation-entry';
import { useShowLoginButtonBasedOnSurveyContent } from '@common/use-cases/use-show-login-button-based-on-survey-content.ts';
import { isUserEmailVerified } from '@common/utils/email-verification';
import { truncatedString } from '@common/utils/string-truncation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@konova/ui/button';
import { Combobox, ComboboxOption } from '@konova/ui/combobox';
import { SeparatedList } from '@konova/ui/separated-list';
import { SpaceAdminRoutes } from '@routes/space-admin-routes.ts';
import { SurveyRoutes } from '@routes/survey-routes.ts';
import clsx from 'clsx';
import { FC, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useLocation, useNavigate } from 'react-router';
import AccountModal from 'src/components/Header/AccountModal';
import AddSpaceModal from 'src/components/Header/AddSpaceModal';
import {
  INavigationItem,
  ISpace,
  ISurvey,
  ISurveyDocumentGroup
} from 'src/types';
import { getPosition, isMobile, isTablet, slugify } from 'src/utils';
import { DocumentGroupService } from '../../services/DocumentGroupService';
import BrandLogo from '../BrandLogo/BrandLogo';
import EmailVerificationAlert from '../EmailVerificationAlert/EmailVerificationAlert';
import ConfirmationModal from './ConfirmationModal.tsx';
import './styles.scss';
import { useHeaderContext } from '@common/context/header-context/index.ts';
import { useSpaceContext } from '@common/context/space/index.ts';
import { useNavigationItemsContext } from '@common/context/navigation-items-context/index.ts';
import { SpaceRoutes } from '@routes/space-routes.ts';

interface IHeader {
  loading: boolean;
  hostname: string;
  survey: ISurvey | null;
  spaceLoading: boolean;
  setIsSentryEnabled: (sentryState: boolean) => void;
  emailVerificationActivated: boolean;
  twoFaActivated: boolean;
  onNavBarBrandClick?: () => void;
}

const Header: FC<IHeader> = ({
  loading,
  onNavBarBrandClick,
  hostname,
  survey,
  spaceLoading,
  setIsSentryEnabled,
  emailVerificationActivated,
  twoFaActivated,
}) => {
  const headerContext = useHeaderContext();
  const spaceContext = useSpaceContext();
  const { t } = useTranslation();
  const logOut = useLogOut();
  const { user, loadUser } = useUserContext();
  const { handleRequest } = useLegacyFunction();
  const { language: lang, languages } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isProjectOverviewPage = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;
  const splitUrlBySlash = location.pathname.split('/');
  const isSupportPage =
    splitUrlBySlash[splitUrlBySlash.length - 1] === 'support' ||
    splitUrlBySlash[splitUrlBySlash.length - 2] === 'support';
  const isAdminSupportPage = !survey && isSupportPage;
  const mailNotVerifiedCountContext = useMailNotVerifiedCountContext();
  const [expanded, setExpanded] = useState(false);
  const subNavigationContext = useSubNavigationContext();
  const [width, setWidth] = useState<string>('');
  const [accountModal, toggleAccountModal] = useState<boolean>(false);
  const [activeNavigationItem, setActiveNavigationItem] = useState<INavigationItem | undefined>(undefined);
  const [showAddSpaceModal, setShowAddSpaceModal] = useState<boolean>(false);
  const [selectedSpace, setSelectedSpace] = useState<ComboboxOption<number> | null>(null);
  const [isLandScape, setIsLandScape] = useState<boolean>(window.innerHeight > window.innerWidth ? false : true);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(window.innerWidth < 992);
  const [newPhone, setNewPhone] = useState<string | undefined>(undefined);
  const [isAdminInAnySurvey, setIsAdminInAnySurvey] = useState<boolean>(false);
  const [documentGroups, setDocumentGroups] = useState<ISurveyDocumentGroup[] | null>(null);
  const [spaceNameToAdd, setSpaceNameToAdd] = useState<string | null>(null);
  const showLoginButtonUseCase = useShowLoginButtonBasedOnSurveyContent(survey?.id ?? null);
  const [userHasRightsForContextSwitcher, setUserHasRightsForContextSwitcher] = useState<boolean>(false);
  const { navigationItems } = useNavigationItemsContext();

  useEffect(() => {
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        setIsLandScape(window.innerHeight > window.innerWidth ? false : true);
      }, 0);
    });
    window.addEventListener('resize', () => {
      setIsMenuCollapsed(window.innerWidth < 992);
    });
  }, []);

  useEffect(() => {
    if (spaceLoading) return;

    if (spaceContext.space?.surveys) {
      setIsAdminInAnySurvey(
        spaceContext.space?.surveys.filter(x => x.userRole !== 'participant').length > 0
      );
    }
  }, [spaceContext.space]);

  useEffect(() => {
    setUserHasRightsForContextSwitcher(
      !!user &&
      ['admin', 'editor', 'evaluator'].includes(survey?.userRole || 'unauthenticated')
    );
  }, [user, survey?.userRole]);

  //TODO: Temporary solution, need to transfer data to Redux
  const getDocumentGroups = () => {
    handleRequest(
      DocumentGroupService.index({ filter: { survey_id: survey?.id } }, 1, 999999)
    ).then(res => {
      setDocumentGroups(res.body.data);
    });
  };

  useEffect(() => {
    if (survey?.id && location.pathname.includes('/participant/survey-document-groups')) {
      getDocumentGroups();
    }
  }, [survey]);

  useEffect(() => {
    const loadingBarWidth = loading ? `${Math.floor(Math.random() * 100) + 10}%` : '100%';
    setWidth(loadingBarWidth);
  }, [loading]);

  useEffect(() => {
    setActiveNavigationItem(
      navigationItems.find(navigationItem => {
        return (
          location.pathname === navigationItem.href ||
          navigationItem.children?.find(
            navigationItem => navigationItem.href === location.pathname
          )
        );
      })
    );
  }, [navigationItems, location.pathname]);


  const spaceSelectOptions = useMemo(() => {
    const options = spaceContext.spacesWhereUserIsAdmin?.map(space => {
      if (space.custom_domain === hostname) {
        setSelectedSpace({ value: space.id, label: space.name! });
      }
      return { value: space.id, label: space.name! };
    });
    if (user?.platform_role == 1) {
      options?.push({ value: 0, label: t<string>('pages.panel.index.create-org') });
    }

    return options ?? [];
  }, [spaceContext.spacesWhereUserIsAdmin.length, hostname, user?.platform_role]);

  const onNavbarBrandClick = e => {
    e.preventDefault();
    if (onNavBarBrandClick) {
      onNavBarBrandClick();
      return;
    }
    if (navigationItems.length) {
      navigate(navigationItems[0].href);
      return;
    }
    navigate(generatePath(SpaceRoutes.index, { lang }));
  };

  const onSpaceChanged = option => {
    if (!option) return;
    if (option.value === 0) {
      setShowAddSpaceModal(true);
      return;
    }
    const space = spaceContext.spacesWhereUserIsAdmin?.find(space => space.id === option.value);
    if (space && space.custom_domain !== hostname) {
      window.location.href = `https://${space.custom_domain}/${lang}/admin`;
    }
  };

  const renderEmailVerificationAlert = () => {
    if (!emailVerificationActivated) return null;
    else if (
      isUserEmailVerified(survey, user!, mailNotVerifiedCountContext.count) &&
      survey?.activeStage
    ) {
      return (
        <EmailVerificationAlert
          survey={survey}
          setIsSentryEnabled={setIsSentryEnabled}
        />
      );
    }
    return null;
  };

  const activeDocumentGroups = documentGroup =>
    documentGroup.survey_stage_ids.includes(survey?.activeStage?.id);

  const subEntriesToRender: SubNavigationEntry[] = useMemo(() => {
    if (subNavigationContext.hide === true) {
      return [];
    }

    if (subNavigationContext.overwriteRules === true) {
      return subNavigationContext.entries.map(entry => (
        {
          ...entry,
          title: entry.title ?? entry.label,
          label: truncatedString(entry.label, 0, 26)
        }
      ));
    }

    if (activeNavigationItem?.children?.length) {
      return activeNavigationItem.children.map(subNavigationItem => ({
        url: subNavigationItem.href,
        active: subNavigationItem.href === location.pathname,
        label: truncatedString(subNavigationItem.name, 0, 26),
        title: subNavigationItem.name,
        icon: subNavigationItem.icon,
      }));
    }

    if (
      documentGroups?.some(x => x.show_in_info_section) &&
      location.pathname.includes('/participant/survey-document-groups')
    ) {
      return documentGroups
        ?.filter(x => x.show_in_info_section)
        .filter(activeDocumentGroups)
        .map(entry => ({
          url: generatePath(SurveyRoutes.documentGroup, { lang, surveySlug: survey?.slug ?? '', id: entry.id.toString() }),
          active: location.pathname.endsWith(
            `/survey-document-groups/${entry.id.toString()}`
          ),
          label: truncatedString(entry.name, 0, 26),
          title: entry.name,
        }));
    }

    if (subNavigationContext.entries && subNavigationContext.entries.length > 0) {
      return subNavigationContext.entries;
    }
    return [];
  }, [
    activeNavigationItem,
    location,
    documentGroups,
    survey,
    subNavigationContext.hide,
    subNavigationContext.entries,
    subNavigationContext.overwriteRules,
  ]);

  return (
    <header id="header">
      <Navbar
        as="div"
        role={'none'}
        expanded={expanded}
        expand="xl"
        variant="light"
        className={`light-header  header-wrapper ${spaceContext.space?.logo_path ? 'space-logo' : null}`}
      >
        <Container className="navbar-container">
          <AnonymousContextConfirmationModal>
            <Navbar.Brand onClick={onNavbarBrandClick}>
              {!spaceLoading ? <BrandLogo space={spaceContext.space} /> : null}
            </Navbar.Brand>
          </AnonymousContextConfirmationModal>

          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav as="nav" role="navigation" aria-label={t('common.aria.main-navigation')}>
              {navigationItems
                .filter(navigationItem => navigationItem.name !== '')
                .map((navigationItem, key) => (
                  <Nav.Link
                    as={Link}
                    className={clsx({ 'active-nav-item': navigationItem === activeNavigationItem }, `nav-item-${slugify(navigationItem.name)}`)}
                    key={`main-nav-${key}`}
                    to={navigationItem.href}
                    onClick={e => {
                      setExpanded(false);
                      if (navigationItem.onClick && navigationItem.href === '#') {
                        e.preventDefault();
                        navigationItem.onClick();
                      }
                    }}
                    aria-current={navigationItem === activeNavigationItem ? 'page' : undefined}
                    eventKey={key}
                  >
                    {navigationItem.icon}
                    &nbsp;
                    {navigationItem.name}
                  </Nav.Link>
                ))}
              {!user?.spaceUser ? (
                (
                  location.pathname.split('/').indexOf('login') > 0 ||
                    location.pathname.split('/').includes('information') ||
                    isProjectOverviewPage ||
                    isAdminSupportPage ? null : isMobile() ||
                    (isTablet() && !isLandScape && isMenuCollapsed) ||
                  (!isTablet() && isMenuCollapsed)
                ) ? (
                  <>
                    {showLoginButtonUseCase &&
                      <div className="login-button">
                        <Button
                          prefix={<FontAwesomeIcon icon={['far', 'sign-in']} />}
                          onClick={() => {
                            navigate(
                              survey?.slug ? generatePath(SurveyRoutes.login, { lang, surveySlug: survey.slug }) : generatePath(SpaceAdminRoutes.login, { lang: lang })
                            );
                          }}
                        >
                          {t<string>('common.sign-in')}
                        </Button>
                      </div>}
                  </>
                ) : null
              ) : null}
            </Nav>
            {survey && userHasRightsForContextSwitcher && (
              <ContextSwitcherButton survey={survey} user={user} />
            )}
          </Navbar.Collapse>

          {headerContext.showSpacesSearch ? (
            <div className={'space-choose-wrapper'}>
              {spaceSelectOptions.length ? (
                <Combobox<number>
                  clearable={false}
                  onCreate={query => {
                    onSpaceChanged({ value: 0 });
                    setSpaceNameToAdd(query);
                  }}
                  items={spaceSelectOptions}
                  value={selectedSpace?.value} placeholder={selectedSpace?.label}
                  onChange={onSpaceChanged}
                />
              ) : (
                <div className="clickable" onClick={() => onSpaceChanged({ value: 0 })}>
                  {t<string>('pages.panel.add-space-modal.create-org')}
                </div>
              )}

              <AddSpaceModal
                handleClose={() => {
                  setShowAddSpaceModal(false);
                  setSpaceNameToAdd(null);
                }}
                show={showAddSpaceModal}
                lang={lang}
                handleRequest={handleRequest}
                name={spaceNameToAdd ?? undefined}
              />
            </div>
          ) : null}

          {languages?.length > 1 ? (
            <nav className="lang-switcher" aria-label={t('common.lang-switcher')}>
              <SeparatedList>
                {languages.map((lng, index) => (
                  <a
                    key={`lang-${index}`}
                    href={`/${lng}${location.pathname?.slice(getPosition(location.pathname, '/', 2))}`}
                    onClick={() => localStorage.setItem('userLanguage', lng)}
                    className={clsx({ 'active-lang': lng === lang })}
                    aria-current={lng === lang ? 'true' : undefined}
                    lang={lng}
                    hrefLang={lng}
                  >
                    <span aria-hidden="true">{lng}</span>
                    <span className="visually-hidden" lang={lng}>{t(`common.${lng}`)}</span>
                  </a>
                ))}
              </SeparatedList>
            </nav>
          ) : null}

          {navigationItems.length ? (
            <div className="user-info-outer-wrapper sm-user-wrapper">
              {user && <ProfileButton onClick={() => toggleAccountModal(true)} aria-label={t('common.aria.profile')} />}
              <Navbar.Toggle
                aria-controls="responsive-navbar-nav"
                onClick={() => setExpanded(!expanded)}
              >
                <FontAwesomeIcon icon={['fal', 'bars']} />
              </Navbar.Toggle>
            </div>
          ) : null}

          {user?.spaceUser?.activated_at ? (
            <div className="md-user-info">
              <ProfileButton onClick={() => toggleAccountModal(true)} />
            </div>
          ) : location.pathname.split('/').indexOf('login') > 1 ||
            location.pathname.split('/').includes('information') ||
            isProjectOverviewPage ||
            isAdminSupportPage ? null : !isMobile() &&
              ((isTablet() && isLandScape) ||
                (isTablet() && !isMenuCollapsed) ||
                (!isTablet() && !isMenuCollapsed)) ? (
            <>
              {showLoginButtonUseCase &&
                <div className="login-button">
                  <Button
                    variant='text'
                    prefix={<FontAwesomeIcon icon={['far', 'sign-in']} />}
                    onClick={() => {
                      navigate(
                        survey?.slug ? generatePath(SurveyRoutes.login, { lang, surveySlug: survey.slug }) : generatePath(SpaceAdminRoutes.login, { lang: lang })
                      );
                    }}
                  >
                    {t<string>('common.sign-in')}
                  </Button>
                </div>
              }
            </>
          ) : null}
        </Container>
      </Navbar>

      {subEntriesToRender.length > 0 && (
        <DocumentNavigationBar entries={subEntriesToRender} />
      )}

      {renderEmailVerificationAlert()}

      {user?.spaceUser && accountModal && (
        <AccountModal
          urlLang={lang}
          handleClose={() => {
            toggleAccountModal(false);
          }}
          logout={async () => {
            toggleAccountModal(false);
            await logOut();
          }}
          spaceUser={{ ...user.spaceUser, email_to_verify: user?.email_to_verify }}
          isSimplifiedRegistrationActive={survey?.simplified_registration_is_activated === 1}
          emailVerificationActivated={emailVerificationActivated}
          user={user}
          loadUser={loadUser}
          handleRequest={handleRequest}
          twoFaActivated={twoFaActivated}
          survey={survey}
          isAdminInAnySurvey={isAdminInAnySurvey}
          onNewPhone={phone => setNewPhone(phone)}
        />
      )}
      {newPhone && (
        <ConfirmationModal
          number={newPhone}
          onClose={() => setNewPhone(undefined)}
          handleRequest={handleRequest}
          afterSubmit={async () => {
            setNewPhone(undefined);
            await loadUser();
          }}
        />
      )}
    </header>
  );
};

export default Header;